import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '@/App';
import { toast } from 'sonner';
import { Server, Plus, Search, Filter, LayoutGrid, Table, Globe, Database, Smartphone, Cpu, ExternalLink, Trash2, Radar, CheckSquare } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Checkbox } from '@/components/ui/checkbox';
import { useTheme } from '@/contexts/ThemeContext';

export default function Assets() {
  const navigate = useNavigate();
  const { isDarkMode } = useTheme();
  const [assets, setAssets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [typeFilter, setTypeFilter] = useState('all');
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [activeTab, setActiveTab] = useState('grid');
  const [selectedAssets, setSelectedAssets] = useState(new Set());
  const [newAsset, setNewAsset] = useState({
    name: '',
    type: 'web',
    url: '',
    ip_address: '',
  });

  useEffect(() => {
    fetchAssets();
  }, []);

  const fetchAssets = async () => {
    try {
      const response = await api.get('/assets');
      setAssets(response.data);
    } catch (error) {
      toast.error('Failed to fetch assets');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateAsset = async (e) => {
    e.preventDefault();
    try {
      await api.post('/assets', newAsset);
      toast.success('Asset created successfully');
      setIsDialogOpen(false);
      setNewAsset({ name: '', type: 'web', url: '', ip_address: '' });
      fetchAssets();
    } catch (error) {
      toast.error('Failed to create asset');
    }
  };

  // Bulk selection functions
  const toggleAssetSelection = (assetId) => {
    setSelectedAssets(prev => {
      const newSet = new Set(prev);
      if (newSet.has(assetId)) {
        newSet.delete(assetId);
      } else {
        newSet.add(assetId);
      }
      return newSet;
    });
  };

  const toggleSelectAllAssets = () => {
    const allIds = new Set(filteredAssets.map(a => a.id));
    const allSelected = filteredAssets.every(a => selectedAssets.has(a.id));
    if (allSelected) {
      setSelectedAssets(new Set());
    } else {
      setSelectedAssets(allIds);
    }
  };

  const handleBulkDeleteAssets = async () => {
    if (selectedAssets.size === 0) return;
    if (!window.confirm(`Are you sure you want to delete ${selectedAssets.size} asset(s)?`)) return;
    
    let successCount = 0;
    let failCount = 0;
    
    for (const id of selectedAssets) {
      try {
        await api.delete(`/assets/${id}`);
        successCount++;
      } catch (error) {
        failCount++;
      }
    }
    
    if (successCount > 0) {
      toast.success(`Deleted ${successCount} asset(s)`);
    }
    if (failCount > 0) {
      toast.error(`Failed to delete ${failCount} asset(s)`);
    }
    
    setSelectedAssets(new Set());
    fetchAssets();
  };

  const handleBulkScanAssets = async () => {
    if (selectedAssets.size === 0) return;
    
    let successCount = 0;
    let failCount = 0;
    
    for (const id of selectedAssets) {
      try {
        await api.post(`/assets/${id}/scan`);
        successCount++;
      } catch (error) {
        failCount++;
      }
    }
    
    if (successCount > 0) {
      toast.success(`Initiated scan for ${successCount} asset(s)`);
    }
    if (failCount > 0) {
      toast.error(`Failed to scan ${failCount} asset(s)`);
    }
    
    setSelectedAssets(new Set());
  };

  const getAssetIcon = (type) => {
    const icons = { web: Globe, api: Server, mobile: Smartphone, infrastructure: Cpu, database: Database };
    return icons[type] || Server;
  };

  const getTypeColor = (type) => {
    const colors = {
      web: 'bg-blue-500/20 text-blue-400 border-blue-500/30',
      api: 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30',
      mobile: 'bg-violet-500/20 text-violet-400 border-violet-500/30',
      infrastructure: 'bg-orange-500/20 text-orange-400 border-orange-500/30',
      database: 'bg-red-500/20 text-red-400 border-red-500/30',
    };
    return colors[type] || colors.web;
  };

  const filteredAssets = assets.filter((asset) => {
    const matchesSearch = asset.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         (asset.url && asset.url.toLowerCase().includes(searchTerm.toLowerCase())) ||
                         (asset.ip_address && asset.ip_address.includes(searchTerm));
    const matchesType = typeFilter === 'all' || asset.type === typeFilter;
    return matchesSearch && matchesType;
  });

  const stats = {
    total: assets.length,
    web: assets.filter(a => a.type === 'web').length,
    api: assets.filter(a => a.type === 'api').length,
    infrastructure: assets.filter(a => a.type === 'infrastructure').length,
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-indigo-400 font-rajdhani text-xl">Loading assets...</div>
      </div>
    );
  }

  return (
    <div data-testid="assets-container" className="space-y-5">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-rajdhani font-bold text-3xl text-white text-shadow-neon">
            Asset Inventory
          </h1>
          <p className="text-zinc-400 font-inter text-sm mt-1">Manage and monitor your digital assets</p>
        </div>

        <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
          <DialogTrigger asChild>
            <Button data-testid="add-asset-button" className="bg-indigo-600 hover:bg-indigo-500 font-rajdhani shadow-neon-indigo">
              <Plus className="w-4 h-4 mr-2" />
              Add Asset
            </Button>
          </DialogTrigger>
          <DialogContent className="bg-zinc-900 border-white/10 text-white">
            <DialogHeader>
              <DialogTitle className="font-rajdhani text-2xl">Add New Asset</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleCreateAsset} className="space-y-4 mt-4">
              <div>
                <Label htmlFor="name" className="text-zinc-300">Name</Label>
                <Input
                  id="name"
                  data-testid="asset-name-input"
                  value={newAsset.name}
                  onChange={(e) => setNewAsset({ ...newAsset, name: e.target.value })}
                  className="bg-zinc-950/50 border-white/10 text-white mt-2"
                  required
                />
              </div>
              <div>
                <Label htmlFor="type" className="text-zinc-300">Type</Label>
                <Select value={newAsset.type} onValueChange={(value) => setNewAsset({ ...newAsset, type: value })}>
                  <SelectTrigger data-testid="asset-type-select" className="bg-zinc-950/50 border-white/10 text-white mt-2">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-zinc-900 border-white/10 text-white">
                    <SelectItem value="web">Web Application</SelectItem>
                    <SelectItem value="api">API</SelectItem>
                    <SelectItem value="mobile">Mobile App</SelectItem>
                    <SelectItem value="infrastructure">Infrastructure</SelectItem>
                    <SelectItem value="database">Database</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="url" className="text-zinc-300">URL (optional)</Label>
                <Input
                  id="url"
                  data-testid="asset-url-input"
                  value={newAsset.url}
                  onChange={(e) => setNewAsset({ ...newAsset, url: e.target.value })}
                  className="bg-zinc-950/50 border-white/10 text-white mt-2"
                  placeholder="https://..."
                />
              </div>
              <div>
                <Label htmlFor="ip" className="text-zinc-300">IP Address (optional)</Label>
                <Input
                  id="ip"
                  data-testid="asset-ip-input"
                  value={newAsset.ip_address}
                  onChange={(e) => setNewAsset({ ...newAsset, ip_address: e.target.value })}
                  className="bg-zinc-950/50 border-white/10 text-white mt-2"
                  placeholder="192.168.x.x"
                />
              </div>
              <Button data-testid="submit-asset-button" type="submit" className="w-full bg-indigo-600 hover:bg-indigo-500">
                Create Asset
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Stats Row */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <div className="glass rounded-xl p-4 text-center">
          <p className="text-2xl font-rajdhani font-bold text-white">{stats.total}</p>
          <p className="text-xs text-zinc-500">Total Assets</p>
        </div>
        <div className="glass rounded-xl p-4 text-center border-l-2 border-blue-500">
          <p className="text-2xl font-rajdhani font-bold text-blue-400">{stats.web}</p>
          <p className="text-xs text-zinc-500">Web Apps</p>
        </div>
        <div className="glass rounded-xl p-4 text-center border-l-2 border-emerald-500">
          <p className="text-2xl font-rajdhani font-bold text-emerald-400">{stats.api}</p>
          <p className="text-xs text-zinc-500">APIs</p>
        </div>
        <div className="glass rounded-xl p-4 text-center border-l-2 border-orange-500">
          <p className="text-2xl font-rajdhani font-bold text-orange-400">{stats.infrastructure}</p>
          <p className="text-xs text-zinc-500">Infrastructure</p>
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <div className="flex items-center justify-between mb-4">
          <TabsList className="bg-zinc-900/50 border border-white/10">
            <TabsTrigger 
              value="grid" 
              className="data-[state=active]:bg-indigo-600/20 data-[state=active]:text-indigo-400"
              data-testid="tab-grid"
            >
              <LayoutGrid className="w-4 h-4 mr-2" />
              Grid View
            </TabsTrigger>
            <TabsTrigger 
              value="table" 
              className="data-[state=active]:bg-indigo-600/20 data-[state=active]:text-indigo-400"
              data-testid="tab-table"
            >
              <Table className="w-4 h-4 mr-2" />
              Table View
            </TabsTrigger>
          </TabsList>

          <div className="flex gap-3">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
              <Input
                data-testid="search-assets-input"
                placeholder="Search assets..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 w-64 bg-zinc-950/50 border-white/10 text-white"
              />
            </div>
            <Select value={typeFilter} onValueChange={setTypeFilter}>
              <SelectTrigger data-testid="filter-type-select" className="w-48 bg-zinc-950/50 border-white/10 text-white">
                <Filter className="w-4 h-4 mr-2" />
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-zinc-900 border-white/10 text-white">
                <SelectItem value="all">All Types</SelectItem>
                <SelectItem value="web">Web Applications</SelectItem>
                <SelectItem value="api">APIs</SelectItem>
                <SelectItem value="mobile">Mobile Apps</SelectItem>
                <SelectItem value="infrastructure">Infrastructure</SelectItem>
                <SelectItem value="database">Databases</SelectItem>
              </SelectContent>
            </Select>
            <Button
              variant="outline"
              size="sm"
              onClick={toggleSelectAllAssets}
              className="border-white/10 text-zinc-300 hover:bg-white/5"
              data-testid="select-all-assets"
            >
              <CheckSquare className="w-4 h-4 mr-1" />
              {filteredAssets.every(a => selectedAssets.has(a.id)) && filteredAssets.length > 0 ? 'Deselect All' : 'Select All'}
            </Button>
          </div>
        </div>

        {/* Bulk Actions Bar */}
        {selectedAssets.size > 0 && (
          <div className="mb-4 p-3 rounded-lg border bg-zinc-900/50 border-white/10 flex items-center gap-3">
            <span className="text-sm text-zinc-400">
              <strong>{selectedAssets.size}</strong> asset(s) selected
            </span>
            <div className="flex gap-2">
              <Button size="sm" onClick={handleBulkScanAssets} className="bg-emerald-600 hover:bg-emerald-500 text-white text-xs" data-testid="bulk-scan-assets">
                <Radar className="w-3 h-3 mr-1" />
                Scan Selected
              </Button>
              <Button size="sm" variant="outline" onClick={handleBulkDeleteAssets} className="text-xs border-red-500/30 text-red-400 hover:bg-red-500/10" data-testid="bulk-delete-assets">
                <Trash2 className="w-3 h-3 mr-1" />
                Delete Selected
              </Button>
            </div>
          </div>
        )}

        <TabsContent value="grid" className="mt-0">
          {filteredAssets.length === 0 ? (
            <div className="glass rounded-2xl p-12 text-center">
              <Server className="w-16 h-16 text-zinc-600 mx-auto mb-4" />
              <p className="text-zinc-400 font-inter">No assets found</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {filteredAssets.map((asset) => {
                const Icon = getAssetIcon(asset.type);
                return (
                  <div
                    key={asset.id}
                    data-testid={`asset-card-${asset.id}`}
                    className={`glass glass-hover rounded-xl p-5 border transition-all cursor-pointer ${
                      selectedAssets.has(asset.id)
                        ? 'border-indigo-500/50 bg-indigo-500/10'
                        : 'border-white/10 hover:border-indigo-500/30'
                    }`}
                  >
                    <div className="flex items-start gap-4">
                      <Checkbox
                        checked={selectedAssets.has(asset.id)}
                        onCheckedChange={() => toggleAssetSelection(asset.id)}
                        onClick={(e) => e.stopPropagation()}
                        data-testid={`select-asset-${asset.id}`}
                      />
                      <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${getTypeColor(asset.type).split(' ')[0]}`}>
                        <Icon className={`w-6 h-6 ${getTypeColor(asset.type).split(' ')[1]}`} />
                      </div>
                      <div className="flex-1 min-w-0" onClick={() => navigate(`/assets/${asset.id}`)}>
                        <h3 className="font-rajdhani font-semibold text-lg text-white truncate">{asset.name}</h3>
                        <span className={`inline-block px-2 py-0.5 rounded text-xs uppercase border ${getTypeColor(asset.type)}`}>
                          {asset.type}
                        </span>
                      </div>
                    </div>
                    
                    <div className="mt-4 space-y-2 text-sm" onClick={() => navigate(`/assets/${asset.id}`)}>
                      {asset.url && (
                        <div className="flex items-center gap-2 text-zinc-400">
                          <Globe className="w-3 h-3" />
                          <span className="truncate text-xs">{asset.url}</span>
                        </div>
                      )}
                      {asset.ip_address && (
                        <div className="flex items-center gap-2 text-zinc-400">
                          <Server className="w-3 h-3" />
                          <span className="font-mono text-xs">{asset.ip_address}</span>
                        </div>
                      )}
                      <div className="flex items-center gap-2 text-zinc-500 text-xs">
                        <span>Discovered: {new Date(asset.discovered_at).toLocaleDateString()}</span>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </TabsContent>

        <TabsContent value="table" className="mt-0">
          <div className="glass rounded-2xl overflow-hidden">
            <div className="scroll-container">
              <table className="w-full min-w-[800px]" data-testid="assets-table">
                <thead className="bg-zinc-900/70">
                  <tr>
                    <th className="px-4 py-3 w-10"></th>
                    <th className="px-4 py-3 text-left text-xs font-rajdhani font-semibold text-zinc-400 uppercase tracking-wider">Name</th>
                    <th className="px-4 py-3 text-left text-xs font-rajdhani font-semibold text-zinc-400 uppercase tracking-wider">Type</th>
                    <th className="px-4 py-3 text-left text-xs font-rajdhani font-semibold text-zinc-400 uppercase tracking-wider">URL / IP</th>
                    <th className="px-4 py-3 text-left text-xs font-rajdhani font-semibold text-zinc-400 uppercase tracking-wider">Status</th>
                    <th className="px-4 py-3 text-left text-xs font-rajdhani font-semibold text-zinc-400 uppercase tracking-wider">Discovered</th>
                    <th className="px-4 py-3 text-left text-xs font-rajdhani font-semibold text-zinc-400 uppercase tracking-wider">Last Scanned</th>
                    <th className="px-4 py-3 text-right text-xs font-rajdhani font-semibold text-zinc-400 uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-white/5">
                  {filteredAssets.length === 0 ? (
                    <tr>
                      <td colSpan="8" className="px-4 py-12 text-center">
                        <Server className="w-12 h-12 text-zinc-600 mx-auto mb-3" />
                        <p className="text-zinc-400">No assets found</p>
                      </td>
                    </tr>
                  ) : (
                    filteredAssets.map((asset) => {
                      const Icon = getAssetIcon(asset.type);
                      return (
                        <tr 
                          key={asset.id} 
                          className={`transition-colors ${
                            selectedAssets.has(asset.id) ? 'bg-indigo-500/10' : 'hover:bg-white/5'
                          }`}
                          data-testid={`asset-row-${asset.id}`}
                        >
                          <td className="px-4 py-4">
                            <Checkbox
                              checked={selectedAssets.has(asset.id)}
                              onCheckedChange={() => toggleAssetSelection(asset.id)}
                              data-testid={`select-asset-table-${asset.id}`}
                            />
                          </td>
                          <td className="px-4 py-4">
                            <div className="flex items-center gap-3">
                              <div className={`w-8 h-8 rounded flex items-center justify-center ${getTypeColor(asset.type).split(' ')[0]}`}>
                                <Icon className={`w-4 h-4 ${getTypeColor(asset.type).split(' ')[1]}`} />
                              </div>
                              <span className="text-white font-inter text-sm">{asset.name}</span>
                            </div>
                          </td>
                          <td className="px-4 py-4">
                            <span className={`px-2 py-1 rounded text-xs uppercase border ${getTypeColor(asset.type)}`}>
                              {asset.type}
                            </span>
                          </td>
                          <td className="px-4 py-4">
                            <div className="text-zinc-400 text-sm">
                              {asset.url ? (
                                <a 
                                  href={asset.url} 
                                  target="_blank" 
                                  rel="noopener noreferrer"
                                  className="flex items-center gap-1 hover:text-indigo-400"
                                >
                                  <span className="truncate max-w-[200px]">{asset.url}</span>
                                  <ExternalLink className="w-3 h-3" />
                                </a>
                              ) : asset.ip_address ? (
                                <span className="font-mono">{asset.ip_address}</span>
                              ) : (
                                '-'
                              )}
                            </div>
                          </td>
                          <td className="px-4 py-4">
                            <span className={`px-2 py-1 rounded text-xs ${
                              asset.status === 'active' ? 'bg-green-500/20 text-green-400' : 'bg-zinc-500/20 text-zinc-400'
                            }`}>
                              {asset.status || 'active'}
                            </span>
                          </td>
                          <td className="px-4 py-4">
                            <span className="text-zinc-400 text-xs">{new Date(asset.discovered_at).toLocaleDateString()}</span>
                          </td>
                          <td className="px-4 py-4">
                            <span className="text-zinc-400 text-xs">
                              {asset.last_scanned ? new Date(asset.last_scanned).toLocaleDateString() : 'Never'}
                            </span>
                          </td>
                          <td className="px-4 py-4 text-right">
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => navigate(`/assets/${asset.id}`)}
                              className="text-indigo-400 hover:bg-indigo-500/10"
                            >
                              View
                            </Button>
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
            {filteredAssets.length > 0 && (
              <div className="px-4 py-3 bg-zinc-900/50 border-t border-white/5">
                <p className="text-xs text-zinc-500">
                  Showing {filteredAssets.length} of {assets.length} assets
                </p>
              </div>
            )}
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
